import { useCallback, useState } from "react";
import omit from "lodash/omit";

import getDispensariesV3 from "api/requests/consumerApi/getDispensariesV3";
import searchShopCoreMenuItems, {
  ShopCoreMenuItemsApiResponse,
} from "api/requests/consumerApi/searchShopCoreMenuItems";
import { Coordinates } from "custom-types/Coordinates";
import { DispensaryV3 } from "custom-types/DispensariesV3";
import { MenuItemV2 } from "custom-types/MenuItemV2";
import { ShopTypes } from "custom-types/Shop";
import { Store } from "custom-types/Store";
import logError from "utils/logError";
import { mapFilterObjectToAPI } from "utils/mapUIFiltersToAPI";

export type FilterValues = {
  brandReferral?: string;
  deal_active?: string;
  q?: string;
  page?: string;
  product_category?: string;
  searchCategory?: string;
  shopCategory?: ShopTypes;
  shopName?: string;
  slug?: string;
  sort?: string;
  sort_direction?: string;
  strain_category?: string;
  strain_name?: string;
  [key: string]: string | string[] | undefined;
};

export type LocationData = {
  coordinates: Coordinates;
  radius: number;
};

export type MerchDispensary = {
  id: number;
  name: string;
  slug: string;
};

export type HookReturn = {
  search: (filterValues?: FilterValues, q?: string) => Promise<void>;
  menuItems: MenuItemV2[];
  menuItemsLoading: boolean;
  sponsoredDispensaryMap?: { [key: number]: Store };
};

type CampaignType = string;

const convertToStoreMap = (stores: DispensaryV3[]) =>
  stores.reduce(
    (map, store) => ({
      ...map,
      [store.id]: store,
    }),
    {},
  );

export const useGetMerchCampaignMenuItems = ({
  campaignType,
  locationData,
  merchDispensaries = [],
}: {
  campaignType: CampaignType;
  locationData: LocationData;
  merchDispensaries?: MerchDispensary[];
}): HookReturn => {
  const [campaignDispensariesMap, setCampaignDispensariesMap] = useState<{
    [key: number]: Store;
  }>();
  const [menuItems, setMenuItems] = useState<MenuItemV2[]>([]);

  const [loading, setLoading] = useState(true);

  const search = useCallback(
    async (filterValues: FilterValues = {}, q?: string) => {
      async function getMerchCampaignDispensaryMenuItems() {
        setLoading(true);
        let sponsoredStores: MerchDispensary[];

        if (merchDispensaries.length === 0) {
          const { sponsoredStores: result } =
            await getDispensariesByMerchCampaign(campaignType, locationData);
          sponsoredStores = result?.map((dispensary) => ({
            id: dispensary.id,
            name: dispensary.name,
            slug: dispensary.slug as string,
          }));
          setCampaignDispensariesMap(convertToStoreMap(result));
        } else {
          sponsoredStores = merchDispensaries;
        }

        let dispensarySlugs: string[] = [];

        if (Object.keys(filterValues).includes("dispensary_name")) {
          dispensarySlugs = getSlugs(
            sponsoredStores.filter(
              (dispensary) =>
                dispensary.name ===
                decodeURI(filterValues.dispensary_name as string),
            ),
          );
        } else {
          dispensarySlugs = limitResult(getSlugs(sponsoredStores));
        }

        const filters = omit(filterValues, ["dispensary_name"]);

        const result = await getMenuItemsByDispensaries(
          locationData.coordinates,
          dispensarySlugs,
          filters,
          q,
        );
        setMenuItems(result);
        setLoading(false);
      }

      getMerchCampaignDispensaryMenuItems();
    },
    [locationData, campaignType],
  );

  return {
    menuItems,
    menuItemsLoading: loading,
    search,
    sponsoredDispensaryMap: campaignDispensariesMap,
  };
};

export const getMenuItemsByDispensaries = async (
  coordinates: Coordinates,
  dispensarySlugs: string[] = [],
  filters = {},
  q?: string,
): Promise<MenuItemV2[]> => {
  try {
    const getSlugParams = (slug: string) => ({
      // TODO eventually should deprecate in favor of V2
      filters: mapFilterObjectToAPI({ ...filters, dispensary_slug: [slug] }),
      take: 3, // always return at least 3 menu items if possible
    });
    let promises: Promise<ShopCoreMenuItemsApiResponse>[] = [];

    dispensarySlugs.forEach(
      (slug: string) =>
        (promises = [
          ...promises,
          searchShopCoreMenuItems(coordinates.lat, coordinates.lon, {
            ...getSlugParams(slug),
            ...(q ? { q } : {}),
          }),
        ]),
    );

    const result = await Promise.all(promises);

    return processMenuItemsResult(result, dispensarySlugs);
  } catch (error) {
    logError("Error fetching merchandising campaign menu items");

    return [];
  }
};

export const processMenuItemsResult = (
  result: ShopCoreMenuItemsApiResponse[],
  dispensarySlugs: string[],
): MenuItemV2[] => {
  let limit = 0;
  const menuItemsMap: MenuItemV2[][] = result
    .map(({ data: { data } }) => {
      limit = data.length >= limit ? data.length : limit;
      return data;
    })
    .filter((arr) => arr.length > 0);
  const flattenedMenuItems = menuItemsMap.flat();

  if (dispensarySlugs.length === 1 || flattenedMenuItems.length === 0) {
    return flattenedMenuItems;
  }
  let placements: MenuItemV2[] = [];

  const take = flattenedMenuItems.length > 3 ? 3 : flattenedMenuItems.length;

  for (let i = 0; i < limit && take > placements.length; i++) {
    placements = [
      ...placements,
      menuItemsMap
        .flatMap((items) => items[i])
        .filter((placement) => placement),
    ].flat();
  }

  return limitResult(placements);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
const limitResult = (result: any[], take = 3) => result.slice(0, take);

const getSlugs = (dispensaries: MerchDispensary[]) =>
  dispensaries.map(({ slug }: MerchDispensary) => slug);

export const getDispensariesByMerchCampaign = async (
  campaignType: CampaignType,
  locationData: LocationData,
) => {
  const LIMIT = 3;

  try {
    const {
      sponsoredStores = [],
    }: {
      sponsoredStores: DispensaryV3[];
    } = await getDispensariesV3({
      flags: ["delivery"],
      lat: locationData.coordinates.lat,
      lon: locationData.coordinates.lon,
      merchUnitName: campaignType,
      radius: `${locationData.radius}mi`,
      take: LIMIT,
    });

    return { sponsoredStores };
  } catch {
    return { sponsoredStores: [] };
  }
};

export default useGetMerchCampaignMenuItems;
